// src/pages/agents/AgentPromptTab.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, CircularProgress, Alert, Paper,
  Select, MenuItem, FormControl, InputLabel, Stack, Divider,
  Card, CardContent, CardActions, Chip, Tooltip, IconButton, useTheme // Added useTheme
} from '@mui/material';
import {
  Link as LinkIcon, LinkOff as UnlinkIcon, CheckCircle as SetActiveIcon,
  AddCircleOutline as AddIcon, Edit as EditIcon, CheckCircleOutline as CheckCircleIcon, // Changed for active prompt
} from '@mui/icons-material';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { agentService } from '../../api/agentService';
import { promptService } from '../../api/promptService';

const AgentPromptTab = () => {
  const { agent } = useOutletContext(); // Agent data from parent layout
  const { agentId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme(); // For styling active prompt card

  const [activePrompt, setActivePrompt] = useState(null);
  const [linkedPrompts, setLinkedPrompts] = useState([]);
  const [availablePrompts, setAvailablePrompts] = useState([]); // Prompts not yet linked to this agent

  const [selectedPromptToLink, setSelectedPromptToLink] = useState('');

  const [loadingActive, setLoadingActive] = useState(true);
  const [loadingLinked, setLoadingLinked] = useState(true);
  const [loadingAvailable, setLoadingAvailable] = useState(true);
  const [actionInProgress, setActionInProgress] = useState(null); // Tracks ID of prompt being actioned
  const [error, setError] = useState(null);

  const fetchAgentPromptsData = useCallback(async () => {
    if (!agentId) return;

    setLoadingActive(true); setLoadingLinked(true); setLoadingAvailable(true);
    // setError(null); // Clear general error on new fetch attempt

    let activePromptData = null;
    let linkedPromptsData = [];
    let allSystemPrompts = []; // All prompts in the system
    let fetchError = null;

    try {
      const [activeRes, linkedRes, allPromptsRes] = await Promise.all([
        agentService.getActivePromptForAgent(agentId),
        agentService.listPromptsForAgent(agentId),
        promptService.getAllPrompts() // Get all prompts to determine available ones
      ]);

      // Process Active Prompt
      if (activeRes?.isError) {
        if (activeRes.status === 404) { // 404 is not an error, just no active prompt
          activePromptData = null;
        } else {
          fetchError = (fetchError || '') + `Failed to get active prompt: ${activeRes.message}. `;
        }
      } else {
        activePromptData = activeRes;
      }
      setActivePrompt(activePromptData);
      setLoadingActive(false);

      // Process Linked Prompts
      if (linkedRes?.isError) {
        fetchError = (fetchError || '') + `Failed to get linked prompts: ${linkedRes.message}. `;
      } else {
        linkedPromptsData = Array.isArray(linkedRes) ? linkedRes : [];
      }
      setLinkedPrompts(linkedPromptsData);
      setLoadingLinked(false);

      // Process Available Prompts (all prompts MINUS currently linked ones)
      if (allPromptsRes?.isError) {
        fetchError = (fetchError || '') + `Failed to get all prompts: ${allPromptsRes.message}.`;
      } else {
        allSystemPrompts = Array.isArray(allPromptsRes) ? allPromptsRes : [];
      }
      // Update available prompts based on the latest linkedPromptsData
      const currentLinkedIds = new Set(linkedPromptsData.map(p => p.id));
      setAvailablePrompts(allSystemPrompts.filter(p => !currentLinkedIds.has(p.id)));
      setLoadingAvailable(false);

      if (fetchError) {
        setError(fetchError.trim());
        // toast.error(fetchError.trim()); // Optionally toast general fetch error
      } else {
        setError(null); // Clear error if all fetches were okay (or 404 for active)
      }

    } catch (err) {
      console.error("Error in fetchAgentPromptsData:", err);
      const errMsg = `Error loading prompt data: ${err.message}`;
      setError(errMsg);
      // toast.error(errMsg);
      setLoadingActive(false); setLoadingLinked(false); setLoadingAvailable(false);
    }
  }, [agentId]);

  useEffect(() => {
    fetchAgentPromptsData();
  }, [fetchAgentPromptsData]);

  const handleLinkPrompt = async () => {
    if (!selectedPromptToLink) {
      toast.warn('Please select a prompt to link.');
      return;
    }
    setActionInProgress(selectedPromptToLink);
    setError(null);
    const res = await agentService.linkPromptToAgent(agentId, selectedPromptToLink);
    if (res.isError) {
      toast.error(`Failed to link prompt: ${res.message}`);
    } else {
      toast.success('Prompt linked successfully.');
      setSelectedPromptToLink(''); // Clear selection
      await fetchAgentPromptsData(); // Refresh all prompt data for the agent
    }
    setActionInProgress(null);
  };

  const handleUnlinkPrompt = async (promptIdToUnlink) => {
    const promptToUnlink = linkedPrompts.find(p => p.id === promptIdToUnlink);
    if (!window.confirm(`Are you sure you want to unlink the prompt "${promptToUnlink?.name || 'this prompt'}"? ${promptIdToUnlink === activePrompt?.id ? "It is currently active and will be deactivated." : ""}`)) {
        return;
    }
    setActionInProgress(promptIdToUnlink);
    setError(null);
    const res = await agentService.unlinkPromptFromAgent(agentId, promptIdToUnlink);
    if (res.isError) {
      toast.error(`Failed to unlink prompt: ${res.message}`);
    } else {
      toast.success('Prompt unlinked successfully.');
      await fetchAgentPromptsData();
    }
    setActionInProgress(null);
  };

  const handleSetActivePrompt = async (promptIdToActivate) => {
    if (promptIdToActivate === activePrompt?.id) {
        toast.info("This prompt is already active.");
        return;
    }
    setActionInProgress(promptIdToActivate);
    setError(null);
    const res = await agentService.setActivePromptForAgent(agentId, promptIdToActivate);
    if (res.isError) {
      toast.error(`Failed to set active prompt: ${res.message}`);
    } else {
      toast.success('Prompt set as active.');
      await fetchAgentPromptsData();
    }
    setActionInProgress(null);
  };

  const initialLoading = loadingActive && loadingLinked && loadingAvailable && !error;

  if (initialLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
        <Typography sx={{ml: 2}}>Loading prompt configurations...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Active Prompt Section */}
      <Paper sx={{ p: {xs:2, md:3}, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Current Active Prompt</Typography>
        {loadingActive && !activePrompt && !error ? <CircularProgress size={24} /> : // Show loader if active is loading, no active prompt yet, and no general error
          activePrompt ? (
            <Card variant="outlined" sx={{backgroundColor: theme.palette.mode === 'dark' ? theme.palette.success.dark : theme.palette.success.lightest }}>
              <CardContent>
                <Typography variant="h5" component="div" gutterBottom sx={{color: theme.palette.success.contrastText}}>
                  {activePrompt.name}
                  <Chip label="Active" color="success" size="small" sx={{ ml: 1, backgroundColor: theme.palette.common.white, color: theme.palette.success.dark }} icon={<CheckCircleIcon />} />
                </Typography>
                <Typography variant="body2" sx={{
                    maxHeight: '6em', overflow: 'hidden', textOverflow: 'ellipsis',
                    display: '-webkit-box', WebkitLineClamp: '3', WebkitBoxOrient: 'vertical',
                    color: theme.palette.success.contrastText, opacity: 0.9
                  }}>
                  {activePrompt.content}
                </Typography>
              </CardContent>
              <CardActions sx={{justifyContent: 'flex-end', pt: 0}}>
                 <Button 
                    size="small" 
                    startIcon={<EditIcon/>} 
                    onClick={() => navigate(`/prompts/edit/${activePrompt.id}`)}
                    disabled={!!actionInProgress}
                    sx={{color: theme.palette.success.contrastText, '&:hover': {backgroundColor: 'rgba(255,255,255,0.1)'}}}
                  >
                    Edit Prompt
                  </Button>
                 <Button 
                    size="small" 
                    color="warning" // Keep warning for destructive action
                    startIcon={actionInProgress === activePrompt.id ? <CircularProgress size={16} color="inherit"/> :<UnlinkIcon/>} 
                    onClick={() => handleUnlinkPrompt(activePrompt.id)} 
                    disabled={!!actionInProgress}
                    // sx={{color: theme.palette.warning.contrastText, '&:hover': {backgroundColor: 'rgba(0,0,0,0.1)'}}} // If want different hover on warning
                  >
                    Unlink & Deactivate
                 </Button>
              </CardActions>
            </Card>
          ) : (
            !loadingActive && !error && <Alert severity="info">No prompt is currently active for this agent. Select one from the linked prompts below or link a new one.</Alert>
          )
        }
      </Paper>

      <Divider sx={{ my: 3 }} />

      {/* Link New Prompt Section */}
      <Paper sx={{ p: {xs:2, md:3}, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Link New or Existing Prompt</Typography>
        <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} alignItems="flex-start" mb={2}>
          <FormControl fullWidth sx={{ minWidth: 200 }}>
            <InputLabel id="select-prompt-to-link-label">Select Prompt to Link</InputLabel>
            <Select
              labelId="select-prompt-to-link-label"
              value={selectedPromptToLink}
              label="Select Prompt to Link" // Keep for proper label floating with OutlinedInput
              onChange={(e) => setSelectedPromptToLink(e.target.value)}
              disabled={loadingAvailable || !!actionInProgress || (availablePrompts.length === 0 && !loadingAvailable)}
              displayEmpty
              renderValue={(selected) => {
                if (selected === "") {
                  return <em>{loadingAvailable ? 'Loading...' : (availablePrompts.length === 0 ? 'No prompts to link' : 'Select a prompt')}</em>;
                }
                const selectedPrompt = availablePrompts.find(p => p.id === selected);
                // Ensure selectedPrompt is found before accessing .name
                return selectedPrompt ? selectedPrompt.name : <em>Select a prompt</em>;
              }}
            >
              {/* Conditional MenuItems for list state feedback */}
              {!loadingAvailable && availablePrompts.length === 0 && (
                  <MenuItem value="" disabled>
                      <em>No other prompts available to link</em>
                  </MenuItem>
              )}
              {loadingAvailable && (
                  <MenuItem value="" disabled>
                      <em>Loading available prompts...</em>
                  </MenuItem>
              )}
              
              {/* Actual prompt options */}
              {availablePrompts.map(p => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={handleLinkPrompt}
            disabled={!selectedPromptToLink || !!actionInProgress || loadingAvailable}
            startIcon={actionInProgress === selectedPromptToLink && selectedPromptToLink ? <CircularProgress size={20} color="inherit"/> : <LinkIcon />}
            sx={{ flexShrink: 0, mt: { xs: 1, sm: 0 } }}
          >
            Link Selected
          </Button>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
                Or,
            </Typography>
            <Button
            variant="text"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => navigate('/prompts/create', { state: { fromAgentId: agentId, autoLink: true } })}
            disabled={!!actionInProgress}
            >
            Create & Link New Prompt
            </Button>
        </Stack>
      </Paper>

      <Divider sx={{ my: 3 }} />

      {/* Linked Prompts Section */}
      <Typography variant="h6" gutterBottom>Linked Prompts for "{agent?.name || 'this Agent'}"</Typography>
      {loadingLinked && linkedPrompts.length === 0 && !error ? <CircularProgress size={24}/> :
        !loadingLinked && linkedPrompts.length === 0 && !error ? (
          <Alert severity="info">This agent has no prompts linked yet. Use the section above to link one.</Alert>
        ) : (
          linkedPrompts.length > 0 && // Only render grid if there are prompts, even if there was a minor error elsewhere
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            {linkedPrompts.map(p => (
              <Card key={p.id} variant="outlined" elevation={p.id === activePrompt?.id ? 3 : 1} sx={{borderColor: p.id === activePrompt?.id ? theme.palette.primary.main : undefined}}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" component="div" sx={{fontWeight: p.id === activePrompt?.id ? 'bold' : 'normal'}}>{p.name}</Typography>
                    {p.id === activePrompt?.id && <Chip label="Currently Active" color="primary" size="small" icon={<CheckCircleIcon />} />}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{
                      mt: 1, maxHeight: '4.5em', overflow: 'hidden', textOverflow: 'ellipsis',
                      display: '-webkit-box', WebkitLineClamp: '3', WebkitBoxOrient: 'vertical'
                    }}>
                    {p.content}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end' }}>
                  <Tooltip title="View/Edit Prompt Content">
                    <span>
                      <IconButton 
                        size="small" 
                        onClick={() => navigate(`/prompts/edit/${p.id}`)}
                        disabled={!!actionInProgress}
                      ><EditIcon /></IconButton>
                    </span>
                  </Tooltip>
                  {p.id !== activePrompt?.id && (
                    <Tooltip title="Set as Active Prompt">
                       <span>
                        <IconButton 
                          size="small" 
                          color="primary" 
                          onClick={() => handleSetActivePrompt(p.id)} 
                          disabled={!!actionInProgress || actionInProgress === p.id}
                        >
                          {actionInProgress === p.id ? <CircularProgress size={16}/> : <SetActiveIcon />}
                        </IconButton>
                       </span>
                    </Tooltip>
                  )}
                  <Tooltip title="Unlink Prompt from this Agent">
                    <span>
                      <IconButton 
                        size="small" 
                        color="warning" 
                        onClick={() => handleUnlinkPrompt(p.id)} 
                        disabled={!!actionInProgress || actionInProgress === p.id || (p.id === activePrompt?.id && linkedPrompts.length === 1)} // Cannot unlink last active prompt easily, user should set another active first or be warned. For simplicity, disable.
                      >
                        {actionInProgress === p.id ? <CircularProgress size={16}/> : <UnlinkIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </CardActions>
              </Card>
            ))}
          </Box>
        )
      }
    </Box>
  );
};

export default AgentPromptTab;